import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import EnterpriseModal from '../EnterpriseModal';
import { useToastContext } from '../../context/ToastContext';
import { useSmartTranslation } from '../../hooks/useStudentLabel';
import GroupPicker from '../Groups/GroupPicker';

/**
 * Add a student without leaving the page.
 *
 * The quick-action buttons used to navigate to `/students?new=1`, but no page
 * ever read that query parameter — so the tap dropped the user on a plain list
 * with nothing open and no explanation. A quick action should do the thing
 * where you are: four fields, one confirm, a toast, and you stay put.
 *
 * Deliberately smaller than the full add-student form on StudentsPage. This is
 * the name-and-number capture you do while a parent is standing in front of
 * you; the rest of the profile gets filled in later from the student's page.
 */
export default function QuickAddStudentModal({ isOpen, onClose, onAdded }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { success, error: toastError } = useToastContext();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [groupId, setGroupId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setParentPhone('');
    setGroupId('');
    setFormError(null);
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setFormError(null);

    // The backend rejects a missing first or last name with a 400, so catch it
    // here and say which field is missing rather than surfacing a server error.
    if (!firstName.trim() || !lastName.trim()) {
      setFormError(t('quickAddStudent.nameRequired', {
        defaultValue: 'First and last name are required.',
      }));
      return;
    }

    setIsSaving(true);
    try {
      const res = await api.post('/students', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
        parentPhone: parentPhone.trim() || null,
      });
      const created = res?.data || res;
      // Joining the group is a second call; if it fails the client still
      // exists, so say so rather than reporting the whole add as failed.
      if (groupId && created?.id) {
        try {
          await api.post(`/groups/${groupId}/students`, { studentId: created.id });
        } catch (err) {
          toastError(t('clientGroup.addFailed', { message: err?.response?.data?.message || err?.message || '' }));
        }
      }
      success(t('quickAddStudent.added', {
        name: `${firstName.trim()} ${lastName.trim()}`,
        defaultValue: `${firstName.trim()} ${lastName.trim()} added`,
      }));
      onAdded?.(created);
      reset();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message
        || t('common.error', { defaultValue: 'Something went wrong' });
      setFormError(msg);
      toastError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.6rem 0.7rem',
    borderRadius: 'var(--radius-md, 8px)',
    border: '1px solid var(--color-border, #e5e7eb)',
    background: 'var(--color-surface, #fff)',
    color: 'var(--color-text-primary)',
    // 16px keeps iOS from zooming the page when the field takes focus.
    fontSize: '1rem',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0,
    fontSize: '0.85rem', fontWeight: 600,
  };

  return (
    <EnterpriseModal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      titleIcon={<UserPlus size={20} />}
      title={t('quickAddStudent.title', { defaultValue: 'Add student' })}
      isSubmitting={isSaving}
      dataTestId="quick-add-student-modal"
      footer={
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="quick-add-student-form"
            className="btn btn-primary"
            disabled={isSaving}
            data-testid="quick-add-student-submit"
          >
            {isSaving
              ? t('common.saving', { defaultValue: 'Saving…' })
              : t('quickAddStudent.submit', { defaultValue: 'Add student' })}
          </button>
        </div>
      }
    >
      <form
        id="quick-add-student-form"
        onSubmit={handleSubmit}
        style={{ display: 'grid', gap: '0.85rem', direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <label style={labelStyle}>
          {t('students.firstName', { defaultValue: 'First name' })}
          <input
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={inputStyle}
            data-testid="quick-add-student-first"
          />
        </label>

        <label style={labelStyle}>
          {t('students.lastName', { defaultValue: 'Last name' })}
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={inputStyle}
            data-testid="quick-add-student-last"
          />
        </label>

        <label style={labelStyle}>
          {t('students.phone', { defaultValue: 'Phone' })}
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={inputStyle}
            data-testid="quick-add-student-phone"
          />
        </label>

        <label style={labelStyle}>
          {t('students.parentPhone', { defaultValue: 'Parent phone' })}
          <input
            type="tel"
            inputMode="tel"
            value={parentPhone}
            onChange={(e) => setParentPhone(e.target.value)}
            style={inputStyle}
            data-testid="quick-add-student-parent-phone"
          />
        </label>

        <GroupPicker value={groupId} onChange={setGroupId} disabled={isSaving} hint={t('clientGroup.joinsNow')} />

        {formError && (
          <div
            role="alert"
            style={{ display: 'flex', gap: 6, color: 'var(--danger-600, #dc2626)', fontSize: '0.85rem' }}
            data-testid="quick-add-student-error"
          >
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {formError}
          </div>
        )}
      </form>
    </EnterpriseModal>
  );
}
